/**
 * Feature Toggle Panel - Developer interface for managing feature flags
 */

import React, { useState } from 'react';
import { useFeatures } from '../../contexts/FeatureContext';
import { FEATURE_DESCRIPTIONS, FEATURE_CATEGORIES, FeatureFlags } from '../../config/features';

export function FeatureTogglePanel() {
  const { features, toggleFeature, resetFeatures } = useFeatures();
  const [isOpen, setIsOpen] = useState(false);

  if (!isOpen) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsOpen(true)}
          className="bg-purple-600 hover:bg-purple-700 text-white p-3 rounded-full shadow-lg transition-colors"
          title="Open Feature Toggle Panel"
        >
          🎛️
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-96 max-h-96 overflow-y-auto bg-surface border border-line rounded-lg shadow-xl">
      <div className="p-4 border-b border-line flex items-center justify-between">
        <h3 className="text-lg font-semibold text-ink-strong">🎛️ Feature Toggles</h3>
        <div className="flex space-x-2">
          <button
            onClick={resetFeatures}
            className="px-2 py-1 text-xs bg-surface-hover text-white rounded hover:bg-surface-active transition-colors"
          >
            Reset
          </button>
          <button onClick={() => setIsOpen(false)} className="text-ink-faint hover:text-ink">
            ✕
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {Object.entries(FEATURE_CATEGORIES).map(([categoryName, featureKeys]) => (
          <div key={categoryName}>
            <h4 className="text-sm font-semibold text-ink mb-2 uppercase tracking-wide">
              {categoryName}
            </h4>
            <div className="space-y-2">
              {featureKeys.map((featureKey) => (
                <FeatureToggle
                  key={featureKey}
                  featureKey={featureKey}
                  isEnabled={features[featureKey]}
                  onToggle={() => toggleFeature(featureKey)}
                  description={FEATURE_DESCRIPTIONS[featureKey]}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 border-t border-line bg-page">
        <div className="text-xs text-ink-dim">
          <div>
            Enabled: {Object.values(features).filter(Boolean).length}/{Object.keys(features).length}
          </div>
          <div className="mt-1">Changes are saved automatically</div>
        </div>
      </div>
    </div>
  );
}

interface FeatureToggleProps {
  featureKey: keyof FeatureFlags;
  isEnabled: boolean;
  onToggle: () => void;
  description: string;
}

function FeatureToggle({ featureKey, isEnabled, onToggle, description }: FeatureToggleProps) {
  return (
    <div className="flex items-center justify-between p-2 bg-page rounded">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-ink-strong">{featureKey.replace(/_/g, ' ')}</div>
        <div className="text-xs text-ink-dim truncate">{description}</div>
      </div>
      <button
        onClick={onToggle}
        className={`
          relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
          ${isEnabled ? 'bg-purple-600' : 'bg-surface-alt'}
        `}
      >
        <span
          className={`
            inline-block h-3 w-3 transform rounded-full bg-surface transition-transform
            ${isEnabled ? 'translate-x-5' : 'translate-x-1'}
          `}
        />
      </button>
    </div>
  );
}
